import React, { useState, useEffect, useCallback } from 'react';
import { Handle, Position, useNodes } from '@xyflow/react';
import { Upload, Popconfirm, Card, Select, Input, Button, DatePicker, message, Spin, Tabs, Collapse, Space, Tag, Typography, Form, Switch } from 'antd';
import { UploadOutlined, PictureOutlined, FileImageOutlined, SettingOutlined, DeleteOutlined, ShopOutlined, FormOutlined, FileTextOutlined, UserOutlined, TagsOutlined } from '@ant-design/icons';
import { adminInstance } from '../api/axiosConfig';
import NodeInputHandler from '../common/NodeInputHandler';
import dayjs from 'dayjs';
import ReactQuill from 'react-quill';
import GenericNodeContent from '../common/GenericNodeContent';
import 'react-quill/dist/quill.snow.css';

const { Option } = Select;
const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Text, Title } = Typography;
const { TextArea } = Input;

const PostNode = ({ id, data, isConnectable, nodes }) => {
  // State management with editor preferences
  const [nodeName, setNodeName] = useState(data.nodeName || `Post ${id}`);
  const [postTypes, setPostTypes] = useState([]);
  const [selectedPostType, setSelectedPostType] = useState(data.selectedPostType || null);
  const [postFields, setPostFields] = useState({
    core: [],
    acf: [],
    woocommerce: []
  });
  const [fieldMappings, setFieldMappings] = useState(data.fieldMappings || {});
  const [postStatus, setPostStatus] = useState(data.postStatus || 'publish');
  const [scheduledDate, setScheduledDate] = useState(data.scheduledDate ? dayjs(data.scheduledDate) : null);
  const [loading, setLoading] = useState(false);
  const [editorPreferences, setEditorPreferences] = useState(data.editorPreferences || {});
  const [featuredImage, setFeaturedImage] = useState(data.featuredImage || {
    url: '',
    id: null,
    source: 'external' // or 'upload'
  });
  const [productImages, setProductImages] = useState(data.productImages || []);

  // New state variables for authors and categories
  const [authors, setAuthors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedAuthor, setSelectedAuthor] = useState(data.selectedAuthor || '');
  const [selectedCategories, setSelectedCategories] = useState(data.selectedCategories || []);

  const fetchPostTypes = useCallback(async () => {
    if (data.isViewer) {
      return;
    }

    try {
      setLoading(true);
      const response = await adminInstance.get('/post-types');
      setPostTypes(response.data);
    } catch (error) {
      console.error('Error fetching post types:', error);
      message.error('Failed to fetch post types');
    } finally {
      setLoading(false);
    }
  }, [data.isViewer]);

  const fetchPostFields = useCallback(async (postType) => {
    if (data.isViewer) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await adminInstance.get(`/post-fields/${postType}`);
      
      // Improved field categorization
      const organizedFields = {
        core: [], // WordPress core fields
        acf: [], // Advanced Custom Fields
        custom: [], // Other custom fields
        woocommerce: [] // Actual WooCommerce fields
      };
  
      response.data.forEach(field => {
        if (field.name.startsWith('acf_')) {
          // ACF fields
          organizedFields.acf.push(field);
        } else if (field.name.startsWith('wc_') || 
                   ['_price', '_regular_price', '_sale_price', '_sku', '_stock', 
                    '_stock_status', '_manage_stock', '_backorders', '_weight', 
                    '_length', '_width', '_height', '_virtual', '_downloadable']
                    .includes(field.name)) {
          // Only categorize actual WooCommerce fields
          organizedFields.woocommerce.push(field);
        } else if (field.name.startsWith('_')) {
          // Other custom fields
          organizedFields.custom.push(field);
        } else {
          // Core WordPress fields
          organizedFields.core.push(field);
        }
      });
      
      // Only include WooCommerce fields section if the post type is 'product'
      if (postType !== 'product') {
        delete organizedFields.woocommerce;
      }
      
      setPostFields(organizedFields);
    } catch (error) {
      console.error('Error fetching post fields:', error);
      message.error('Failed to fetch post fields');
    } finally {
      setLoading(false);
    }
  }, [data.isViewer]);

  // New fetch functions for authors and categories
  const fetchAuthors = useCallback(async () => {
    if (data.isViewer) {
      return;
    }

    try {
      const response = await adminInstance.get('/authors');
      setAuthors(response.data);
    } catch (error) {
      console.error('Error fetching authors:', error);
      message.error('Failed to fetch authors');
    }
  }, [data.isViewer]);

  const fetchCategories = useCallback(async (postType) => {
    if (data.isViewer || !postType) {
      return;
    }

    try {
      const response = await adminInstance.get(`/categories/${postType}`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      message.error('Failed to fetch categories');
    }
  }, [data.isViewer]);

  // Effects
  useEffect(() => {
    fetchPostTypes();
    fetchAuthors();
  }, [fetchPostTypes, fetchAuthors]);

  useEffect(() => {
    if (selectedPostType) {
      fetchPostFields(selectedPostType);
      fetchCategories(selectedPostType);
    }
  }, [selectedPostType, fetchPostFields, fetchCategories]);

  useEffect(() => {
    data.updateNodeData(id, {
      nodeName,
      selectedPostType,
      fieldMappings,
      postStatus,
      scheduledDate: scheduledDate?.toISOString(),
      editorPreferences,
      featuredImage,  
      productImages,
      selectedAuthor,
      selectedCategories
    });
  }, [
    data,
    id,
    nodeName,
    selectedPostType,
    fieldMappings,
    postStatus,
    scheduledDate,
    editorPreferences,
    featuredImage,   
    productImages,
    selectedAuthor,
    selectedCategories
  ]);

  // Event handlers
  const handlePostTypeChange = useCallback((value) => {
    setSelectedPostType(value);
    setFieldMappings({});
    setCategories([]); // Clear categories when post type changes
    setSelectedCategories([]); // Clear selected categories
    data.updateNodeData(id, { 
      selectedPostType: value, 
      fieldMappings: {},
      editorPreferences: {}, // Reset editor preferences when post type changes
      selectedCategories: [] // Reset selected categories
    });
  }, [data, id]);

  const handleFieldMapping = (field, value) => {
    setFieldMappings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Helper to get editor preference for a field
  const getEditorPreference = (fieldName) => {
    return editorPreferences[fieldName] || 'plain';
  };

  // Toggle editor type for a field
  const toggleEditor = (fieldName) => {
    setEditorPreferences(prev => ({
      ...prev,
      [fieldName]: prev[fieldName] === 'wysiwyg' ? 'plain' : 'wysiwyg'
    }));
  };

  // Quill editor configuration
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ]
  };

  // Field rendering with improved editor toggle
  const renderFieldInput = (field) => {
    const value = fieldMappings[field.name] || '';
    const editorType = getEditorPreference(field.name);
    
    // Determine if field should have rich text capability
    const supportsRichText = field.type === 'wysiwyg' || field.name === 'post_content';
    
    const inputElement = () => {
      if (supportsRichText && editorType === 'wysiwyg') {
        return (
          <div className="nodrag" style={{ marginBottom: 8 }}>
            <ReactQuill
              value={value}
              onChange={(content) => handleFieldMapping(field.name, content)}
              modules={quillModules}
              style={{ backgroundColor: 'white', minHeight: '100px' }}
            />
          </div>
        );
      } else if (field.type === 'select') {
        return (
          <Select
            className="nodrag"
            value={value}
            onChange={(val) => handleFieldMapping(field.name, val)}
            style={{ width: '100%', marginBottom: 8 }}
          >
            {field.options?.map(opt => (
              <Option key={opt.value} value={opt.value}>{opt.label}</Option>
            ))}
          </Select>
        );
      } else {
        // Use GenericNodeContent for all text inputs and textareas
        return (
          <GenericNodeContent 
            id={id}
            data={{ content: value }}  // Pass the field value as content
            onChange={(_, newData) => handleFieldMapping(field.name, newData.content)}  // Update fieldMappings
            placeholder={`Enter ${field.label.toLowerCase()}...`}
            nodes={nodes}
          />
        );
      }
    };
  
    return (
      <div style={{ marginBottom: 16 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 4 }}>
          <Text strong>{field.label}</Text>
          {supportsRichText && (
            <Switch
              checkedChildren="WYSIWYG"
              unCheckedChildren="Plain"
              checked={editorType === 'wysiwyg'}
              onChange={() => toggleEditor(field.name)}
              size="small"
            />
          )}
        </Space>
        {inputElement()}
      </div>
    );
  };

  const renderFieldGroup = (fields, groupTitle) => {
    if (!fields?.length) return null;
  
    const getIcon = (title) => {
      switch (title) {
        case 'Core Fields':
          return <FileTextOutlined />;
        case 'ACF Fields':
          return <FormOutlined />;
        case 'Custom Fields':
          return <SettingOutlined />; // Add this import from antd
        case 'WooCommerce Fields':
          return <ShopOutlined />;
        default:
          return <FileTextOutlined />;
      }
    };
  
    return (
      <Panel 
        header={
          <Space>
            {getIcon(groupTitle)}
            <Text strong>{groupTitle}</Text>
            <Tag color="blue">{fields.length}</Tag>
          </Space>
        }
      >
        <div className="panel-content-scrollable" style={{ padding: '8px 0' }}>
          {fields.map(field => renderFieldInput(field))}
        </div>
      </Panel>
    );
  };

    // Handle image URL input from previous nodes
    const handleImageInput = (value, type = 'featured') => {
      if (!value) return;
      
      // Set the image data
      if (type === 'featured') {
        setFeaturedImage({
          url: value,
          id: null,
          source: 'external'
        });
        // Clear any content field that might have the URL
        handleFieldMapping('post_content', ''); // Clear post content
        handleFieldMapping('post_excerpt', ''); // Clear excerpt too just in case
      } else if (type === 'product') {
        setProductImages(prev => [...prev, {
          url: value,
          id: null,
          source: 'external'
        }]);
      }
      // Return true to indicate we've handled this input
      return true;
    };
  
    // Handle file upload if needed
    const handleFileUpload = async (info, type = 'featured') => {
      const { file, onSuccess, onError } = info;
      const formData = new FormData();
      formData.append('file', file);
  
      try {
        const response = await adminInstance.post('/upload-attachment', formData);
        
        if (response.data.id) {
          const mediaData = {
            url: response.data.url,
            id: response.data.id,
            source: 'upload'
          };
  
          if (type === 'featured') {
            setFeaturedImage(mediaData);
          } else if (type === 'product') {
            setProductImages(prev => [...prev, mediaData]);
          }
  
          onSuccess(response, file);
          message.success(`${file.name} uploaded successfully`);
        }
      } catch (error) {
        onError(error);
        message.error(`${file.name} upload failed: ${error.message}`);
      }
    };
  
    // Remove media
    const handleRemoveMedia = (index, type = 'featured') => {
      if (type === 'featured') {
        setFeaturedImage({ url: '', id: null, source: null });
      } else if (type === 'product') {
        setProductImages(prev => prev.filter((_, i) => i !== index));
      }
    };
  
    // Render media section
    const renderMediaSection = () => {
      const uploadProps = {
        name: 'file',
        multiple: selectedPostType === 'product',
        showUploadList: false,
        customRequest: ({ file, onSuccess, onError }) => 
          handleFileUpload({ file, onSuccess, onError }, 
            selectedPostType === 'product' ? 'product' : 'featured'),
        accept: 'image/*'
      };
      return (
        <div>
          <Title level={5}>
            {selectedPostType === 'product' ? 'Product Images' : 'Featured Image'}
          </Title>
  
          {/* Image URL Input with NodeInputHandler */}
          <div style={{ marginBottom: 16 }}>
            <Input
              placeholder="Image URL from previous node"
              value={selectedPostType === 'product' ? '' : featuredImage.url}
              onChange={(e) => handleImageInput(e.target.value)}
              style={{ marginBottom: 8 }}
              className="nodrag"
            />
            <NodeInputHandler
              nodeId={id}
              onInsert={(tag) => {
                // Handle the image input and prevent default behavior
                handleImageInput(tag);
                return false; // Prevent the tag from being used elsewhere
              }}
            />
          </div>
  
          {/* Upload option as fallback */}
          <div style={{ marginBottom: 16 }}>
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>
                {selectedPostType === 'product' ? 'Upload Product Image' : 'Upload Featured Image'}
              </Button>
            </Upload>
          </div>
  
          {/* Preview Section */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {selectedPostType === 'product' ? (
              productImages.map((image, index) => (
                <div 
                  key={index}
                  className="media-preview"
                >
                  <img 
                    src={image.url}
                    alt={`Product ${index + 1}`}
                  />
                  <Tag color={image.source === 'external' ? 'blue' : 'green'}>
                    {image.source}
                  </Tag>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveMedia(index, 'product')}
                  />
                </div>
              ))
            ) : (
              featuredImage.url && (
                <div className="media-preview">
                  <img 
                    src={featuredImage.url}
                    alt="Featured"
                  />
                  <Tag color={featuredImage.source === 'external' ? 'blue' : 'green'}>
                    {featuredImage.source}
                  </Tag>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveMedia(0)}
                  />
                </div>
              )
            )}
          </div>
        </div>
      );
    };

  return (
    <Card 
      title={
        <Space>
          <span>Create Post</span>
          <Tag color={postStatus === 'publish' ? 'green' : postStatus === 'draft' ? 'orange' : 'blue'}>
            {postStatus}
          </Tag>
        </Space>
      }
      extra={
        <Popconfirm
        title="Delete Node"
        description="Are you sure you want to delete this node?"
        onConfirm={() => data.onDelete(id)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }} > 
        <Button icon={<DeleteOutlined />} /> </Popconfirm>
      }
      style={{ width: 400 }}
    >
      <Handle type="target" position={Position.Left} isConnectable={isConnectable} />

      <Space direction="vertical" style={{ width: '100%' }}>
        <Input
          placeholder="Node Name"
          value={nodeName}
          onChange={(e) => setNodeName(e.target.value)}
          className="nodrag"
        />

        <Select 
          style={{ width: '100%' }}
          placeholder="Select Post Type"
          onChange={handlePostTypeChange}
          value={selectedPostType}
          loading={loading}
          className="nodrag"
        >
          {postTypes.map(type => (
            <Option key={type.name} value={type.name}>{type.label}</Option>
          ))}
        </Select>

        {selectedPostType && (
          <Tabs defaultActiveKey="fields">
            <TabPane tab="Fields" key="fields">
              {loading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin />
                </div>
              ) : (
              <Collapse defaultActiveKey={['core']} className="field-collapse">
                {renderFieldGroup(postFields.core, 'Core Fields')}
                {postFields.acf?.length > 0 && renderFieldGroup(postFields.acf, 'ACF Fields')}
                {postFields.custom?.length > 0 && renderFieldGroup(postFields.custom, 'Custom Fields')}
                {postFields.woocommerce?.length > 0 && selectedPostType === 'product' && 
                  renderFieldGroup(postFields.woocommerce, 'WooCommerce Fields')}
              </Collapse>
              )}
            </TabPane>

            <TabPane 
              tab={
                <Space>
                  <PictureOutlined />
                  <span>Media</span>
                </Space>
              } 
              key="media"
            >
              {renderMediaSection()}
            </TabPane>
            
            <TabPane 
              tab={
                <Space>
                  <SettingOutlined />
                  <span>Settings</span>
                </Space>
              } 
              key="settings"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Form.Item label="Post Status">
                  <Select 
                    value={postStatus}
                    onChange={setPostStatus}
                    className="nodrag"
                  >
                    <Option value="publish">Publish</Option>
                    <Option value="draft">Draft</Option>
                    <Option value="future">Schedule</Option>
                    <Option value="private">Private</Option>
                  </Select>
                </Form.Item>

                {postStatus === 'future' && (
                  <Form.Item label="Schedule Date">
                    <DatePicker 
                      showTime 
                      onChange={setScheduledDate}
                      value={scheduledDate}
                      style={{ width: '100%' }}
                      disabledDate={current => current && current < dayjs().startOf('day')}
                      className="nodrag"
                    />
                  </Form.Item>
                )}

                <Form.Item 
                  label={
                    <Space>
                      <UserOutlined />
                      <span>Author</span>
                    </Space>
                  }
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Select
                      value={selectedAuthor}
                      onChange={setSelectedAuthor}
                      placeholder="Select author..."
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      className="nodrag"
                      style={{ width: '100%' }}
                    >
                      {authors.map(author => (
                        <Option key={author.id} value={author.id}>
                          {author.name}
                        </Option>
                      ))}
                    </Select>
                    <GenericNodeContent 
                      id={id}
                      data={{ content: selectedAuthor }}
                      onChange={(_, newData) => setSelectedAuthor(newData.content)}
                      placeholder="Or use dynamic author ID from previous node..."
                      nodes={nodes}
                      size="small"
                    />
                  </Space>
                </Form.Item>

                <Form.Item 
                  label={
                    <Space>
                      <TagsOutlined />
                      <span>Categories</span>
                    </Space>
                  }
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Select
                      mode="multiple"
                      value={selectedCategories}
                      onChange={setSelectedCategories}
                      placeholder="Select categories..."
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      className="nodrag"
                      style={{ width: '100%' }}
                      maxTagCount={3}
                      maxTagTextLength={20}
                    >
                      {categories.map(category => (
                        <Option key={category.id} value={category.id}>
                          {category.taxonomy_label && categories.some(c => c.taxonomy !== category.taxonomy) 
                            ? `${category.name} (${category.taxonomy_label})`
                            : category.name
                          }
                        </Option>
                      ))}
                    </Select>
                    <GenericNodeContent 
                      id={id}
                      data={{ content: Array.isArray(selectedCategories) ? selectedCategories.join(',') : selectedCategories }}
                      onChange={(_, newData) => {
                        const value = newData.content;
                        setSelectedCategories(typeof value === 'string' ? value.split(',').map(s => s.trim()).filter(Boolean) : value);
                      }}
                      placeholder="Or use dynamic category IDs/names from previous node (comma-separated)..."
                      nodes={nodes}
                      size="small"
                    />
                  </Space>
                </Form.Item>
              </Space>
            </TabPane>
          </Tabs>
        )}
      </Space>

      <Handle type="source" position={Position.Right} id="a" isConnectable={isConnectable} />
      <style>
        {`
          .media-preview {
            position: relative;
            width: 150px;
            height: 150px;
            border: 1px solid #d9d9d9;
            border-radius: 4px;
            overflow: hidden;
          }
          
          .media-preview img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          
          .media-preview .ant-btn {
            position: absolute;
            top: 4px;
            right: 4px;
            background: rgba(255, 255, 255, 0.8);
          }

          .media-preview .ant-tag {
            position: absolute;
            top: 4px;
            left: 4px;
          }

          /* Fix for macOS scrolling issues in collapsed panels */
          .panel-content-scrollable {
            max-height: 300px;
            overflow-y: auto;
            overflow-x: hidden;
            /* Force scrollbar to be visible on all platforms */
            scrollbar-width: thin;
            scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
          }

          /* Custom scrollbar styles for webkit browsers */
          .panel-content-scrollable::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }

          .panel-content-scrollable::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.05);
            border-radius: 4px;
          }

          .panel-content-scrollable::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.2);
            border-radius: 4px;
          }

          .panel-content-scrollable::-webkit-scrollbar-thumb:hover {
            background: rgba(0, 0, 0, 0.3);
          }

          /* Ensure scrollbar is always visible on macOS */
          .panel-content-scrollable::-webkit-scrollbar-track,
          .panel-content-scrollable::-webkit-scrollbar-thumb {
            visibility: visible !important;
          }

          /* Additional styling for better UX */
          .panel-content-scrollable {
            padding-right: 8px;
            margin-right: -8px;
          }
        `}
      </style>
    </Card>
  );
};

export default PostNode;